import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Icons } from "../../../icons";
import {
  ExternalCharacterCounter,
  StyledTextArea,
  TextAreaContainer,
  TextAreaFooter,
  VoiceButton,
  VoiceButtonContainer,
} from "./TextAreaWithVoice.styles";
import { tokens } from "@/shared/design-system/tokens";

export interface TextAreaWithVoiceProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number; // puede cambiar dinámicamente
  minHeight?: string;
  disabled?: boolean;
  enableVoice?: boolean;
  className?: string;
  id?: string;
  rows?: number;
  autoGrow?: boolean;
  showCharacterCounter?: boolean;
  supportingContent?: React.ReactNode;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

export const TextAreaWithVoice: React.FC<TextAreaWithVoiceProps> = ({
  value,
  onChange,
  placeholder = "Escribí tu texto aquí...",
  maxLength = 5000,
  minHeight = "120px",
  disabled = false,
  enableVoice = true,
  className,
  id,
  rows = 4,
  autoGrow = false,
  showCharacterCounter = true,
  supportingContent,
}) => {
  // Refs
  const maxLengthRef = useRef<number>(maxLength);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const baseTextRef = useRef<string>("");

  // Estado UI
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [interimText, setInterimText] = useState("");

  useEffect(() => {
    maxLengthRef.current = maxLength;
  }, [maxLength]);

  // Config SpeechRecognition (no depende de maxLength; usamos ref)
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition);

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "es-AR";

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalTranscript += transcript;
          else interimTranscript += transcript;
        }

        if (interimTranscript) setInterimText(interimTranscript);

        if (finalTranscript) {
          const newValue =
            baseTextRef.current +
            (baseTextRef.current ? " " : "") +
            finalTranscript;

          if (newValue.length <= maxLengthRef.current) {
            onChange(newValue);
            baseTextRef.current = newValue;
            setInterimText("");
          } else {
            console.warn(
              "📏 Voice excede maxLengthRef.current. Se ignora lo extra.",
            );
          }
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error("Speech recognition error:", event?.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
        setInterimText("");
      };
    }

    return () => {
      recognitionRef.current?.stop?.();
    };
  }, [value, onChange]);

  const handleVoiceToggle = () => {
    if (!speechSupported || disabled) return;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setInterimText("");
    } else {
      const isVoiceDisabled = value.length >= maxLength;
      if (isVoiceDisabled) {
        console.warn(
          "[TextAreaWithVoice] voice disabled: value.length >= resolvedMaxLength",
          value.length,
          maxLength,
        );
        return;
      }

      try {
        baseTextRef.current = value;
        setInterimText("");
        recognitionRef.current?.start();
        setIsListening(true);
      } catch (error) {
        console.error("Error starting speech recognition:", error);
      }
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setInterimText("");
    }

    if (newValue.length <= maxLengthRef.current) {
      onChange(newValue);
      baseTextRef.current = newValue;
    } else {
      // Si el usuario pegó mucho texto, recorto para no pelear con el atributo maxlength del DOM
      const trimmed = newValue.slice(0, maxLengthRef.current);
      console.warn(
        `✂️ Recortado a ${maxLengthRef.current} chars (paste excedía el límite)`,
      );
      onChange(trimmed);
      baseTextRef.current = trimmed;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape" && isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setInterimText("");
    }
  };

  // Render value (combinado con interino solo para mostrar)
  const displayValue =
    value + (interimText ? (value ? " " : "") + interimText : "");

  const characterCount = displayValue.length;
  const isVoiceDisabled = value.length >= maxLength;

  useLayoutEffect(() => {
    if (!autoGrow || !textAreaRef.current) {
      return;
    }

    const textArea = textAreaRef.current;
    textArea.style.height = "auto";
    textArea.style.height = `${textArea.scrollHeight}px`;
  }, [autoGrow, displayValue]);

  return (
    <div className={className}>
      <TextAreaContainer>
        <StyledTextArea
          ref={textAreaRef}
          id={id}
          value={displayValue}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          $minHeight={minHeight}
          $isListening={isListening}
          $autoGrow={autoGrow}
        />

        {enableVoice && speechSupported && (
          <VoiceButtonContainer>
            <VoiceButton
              type="button"
              onClick={handleVoiceToggle}
              disabled={disabled || isVoiceDisabled}
              $isListening={isListening}
              $isDisabled={disabled || isVoiceDisabled}
            >
              <Icons
                name="iconMic"
                width={22}
                height={22}
                color={
                  isListening
                    ? `${tokens.colors.basics[0]}`
                    : `${tokens.colors.blue[900]}`
                }
              />
            </VoiceButton>
          </VoiceButtonContainer>
        )}
      </TextAreaContainer>

      {(supportingContent || showCharacterCounter) && (
        <TextAreaFooter>
          {supportingContent}
          {showCharacterCounter && (
            <ExternalCharacterCounter>
              {characterCount}/{maxLength}
            </ExternalCharacterCounter>
          )}
        </TextAreaFooter>
      )}
    </div>
  );
};

export default TextAreaWithVoice;
